import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all languages for now to prevent dynamic import issues
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import tr from './locales/tr.json';
import nl from './locales/nl.json';

// Resources with all languages
const resources = {
    en: { translation: en },
    es: { translation: es },
    fr: { translation: fr },
    de: { translation: de },
    tr: { translation: tr },
    nl: { translation: nl },
};

// Normalize language codes to our supported languages
const normalizeLanguageCode = (languageCode: string): string => {
    const code = languageCode.toLowerCase();

    // Map regional variants to our supported languages
    if (code.startsWith('en')) return 'en'; // en-US, en-GB, en-CA, etc. -> en
    if (code.startsWith('es')) return 'es'; // es-ES, es-MX, es-AR, etc. -> es
    if (code.startsWith('fr')) return 'fr'; // fr-FR, fr-CA, etc. -> fr
    if (code.startsWith('de')) return 'de'; // de-DE, de-AT, de-CH, etc. -> de
    if (code.startsWith('tr')) return 'tr'; // tr-TR -> tr
    if (code.startsWith('nl')) return 'nl'; // nl-NL, nl-BE -> nl

    return 'en'; // Default fallback
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'en',
        lng: 'en', // Explicitly set initial language to 'en' for consistent SSR/hydration
        debug: false,
        supportedLngs: ['en', 'es', 'fr', 'de', 'tr', 'nl'],

        interpolation: {
            escapeValue: false, // React already escapes values
        },

        detection: {
            order: ['querystring', 'cookie'], // Remove localStorage and navigator to prevent auto-detection
            caches: [], // Disable caching to prevent saved preferences
            lookupQuerystring: 'lng',
            lookupCookie: 'i18next',
            lookupSessionStorage: 'i18nextLng',
            lookupFromPathIndex: 0,
            lookupFromSubdomainIndex: 0,
            // Normalize detected languages
            convertDetectedLanguage: (lng: string) => {
                return normalizeLanguageCode(lng);
            },
        },
    });

// Get API URL (same pattern as api.ts)
// For blog (Astro), we need to handle the case where VITE_API_URL might not be set
// In production, use the main domain API endpoint
const getApiUrl = (): string => {
    if (import.meta.env.DEV) {
        return '';
    }
    // Check if we're running on the blog subdomain
    const isBlog = typeof window !== 'undefined' && window.location.hostname.startsWith('blog.');
    // Use production API URL - either from env or default to main domain
    const apiUrl = import.meta.env.VITE_API_URL || (isBlog ? 'https://minifil.com' : 'http://localhost:3001');
    return apiUrl;
};

// Custom geoip detection function
export const detectLanguageFromGeoIP = async (): Promise<string> => {
    try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/geolocation`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Geolocation API returned ${response.status}`);
        }

        const data = await response.json();
        const countryCode = data.countryCode?.toLowerCase();

        const languageMap: { [key: string]: string } = {
            'es': 'es', // Spain
            'mx': 'es', // Mexico
            'ar': 'es', // Argentina
            'fr': 'fr', // France
            'ca': 'fr', // Canada (French)
            'de': 'de', // Germany
            'at': 'de', // Austria
            'ch': 'de', // Switzerland
            'tr': 'tr', // Turkey
            'nl': 'nl', // Netherlands
            'be': 'nl', // Belgium (Dutch-speaking)
        };

        const detectedLanguage = languageMap[countryCode] || 'en';
        return detectedLanguage;
    } catch (error) {
        console.warn('Failed to detect language from GeoIP:', error);
        return 'en';
    }
};

// Keep the loadLanguage function for backward compatibility but make it a no-op
export const loadLanguage = async () => {
    // All languages are now loaded upfront, so this is a no-op
    return;
};

export default i18n;
